<script lang="ts">
  import { setupConfiguratorContext } from "./contexts";
  import { __, sprintf } from '@wordpress/i18n';
  import DebugWidget from "./components/debug-widget.svelte";
  import ConfiguratorStep from "./components/ConfiguratorStep.svelte";
  import AttributeSelector from "./components/AttributeSelector.svelte";
  import MultiColorSelector from "./components/multi-color-selector.svelte";
  import OplageEnLevering from "./components/OplageEnLevering.svelte";
  import DeliveryOptions from "./components/delivery-options.svelte";
  import type { DeliveryOption } from "../../types";
  import PricingSummary from "./components/PricingSummary.svelte";
  import AddToCartActions from "./components/AddToCartActions.svelte";
  import AdminPriceToggle from "./components/admin-price-toggle.svelte";
  import MaxQuantityModal from "./components/max-quantity-modal.svelte";
  import "./styles/configurator.css";

  type Props = {
    productId: number;
    apiUrl: string;
    isAdmin?: boolean;
    debugEnabled?: boolean;
  };

  let { productId, apiUrl, isAdmin = false, debugEnabled = false }: Props = $props();

  // Show debug widget only for admins when debug is enabled
  const showDebug = $derived(isAdmin && debugEnabled);

  // Max quantity modal state
  let showMaxQuantityModal = $state(false);

  // Setup all contexts - this is the core of the configurator
  const ctx = $derived(setupConfiguratorContext(apiUrl));

  // Derived: attributes with more than 1 option (single-option steps are auto-selected and hidden)
  // Uses attr.options (static term IDs from API) instead of getAvailableOptions() which is empty before data loads
  const visibleAttributes = $derived(
    ctx.productData.attributes.filter(attr => attr.options.length !== 1)
  );

  // Track which step is currently expanded
  let expandedStep = $state<number>(0);

  // Initialize data loading when component mounts
  $effect(() => {
    ctx.productData.loadData().then(() => {
      ctx.selection.initializeSelections();
    });
  });

  // Derived: Get step state for each visible attribute
  function getStepState(index: number): "incomplete" | "active" | "completed" {
    const attr = visibleAttributes[index];
    if (!attr) return "incomplete";

    // For balloon product color attributes, check selectedColors
    if (ctx.productData.isBalloonProduct && attr.type === "color") {
      const hasColors = ctx.selection.selectedColors.length > 0;
      if (hasColors) return "completed";
      if (expandedStep === index) return "active";
      return "incomplete";
    }

    // Standard single-select check
    const hasSelection = ctx.selection.selections[attr.slug];

    if (hasSelection) return "completed";
    if (expandedStep === index) return "active";
    return "incomplete";
  }

  // Derived: Get quantity step state
  const quantityStepState = $derived.by(() => {
    const isBalloon = ctx.productData.isBalloonProduct;
    const allAttributesSelected = visibleAttributes.every((attr) => {
      // For balloon products, color attributes use selectedColors
      if (isBalloon && attr.type === "color") {
        return ctx.selection.selectedColors.length > 0;
      }
      return ctx.selection.selections[attr.slug];
    });

    if (ctx.selection.quantity > 0) return "completed";
    if (expandedStep === visibleAttributes.length && allAttributesSelected) return "active";
    return "incomplete";
  });

  // Derived: Count completed steps (only visible attributes)
  const completedSteps = $derived.by(() => {
    let count = 0;
    const isBalloon = ctx.productData.isBalloonProduct;
    // Count attribute selections (only visible ones)
    for (const attr of visibleAttributes) {
      if (isBalloon && attr.type === "color") {
        if (ctx.selection.selectedColors.length > 0) count++;
      } else {
        if (ctx.selection.selections[attr.slug]) count++;
      }
    }
    // Count quantity selection
    if (ctx.selection.quantity > 0) count++;
    return count;
  });

  // Total steps = visible attributes + quantity
  const totalSteps = $derived(visibleAttributes.length + 1);

  // Scroll to a step after it expands (deferred so DOM has updated)
  function scrollToStep(index: number) {
    setTimeout(() => {
      const stepElements = document.querySelectorAll('.wpc-step');
      const target = stepElements[index];
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  }

  // Handle attribute selection
  function handleAttributeSelect(index: number, attrName: string, value: string) {
    ctx.selection.selectAttribute(attrName, value);

    // If kleurgroep changed, clear color selections (old colors may not belong to new group)
    if (attrName.includes('kleurgroep')) {
      ctx.selection.selectedColors = [];
      // Also clear any regular Kleur attribute selection that may no longer be valid
      for (const attr of visibleAttributes) {
        if (attr.slug !== attrName && attr.slug.toLowerCase().includes('kleur')) {
          ctx.selection.selectAttribute(attr.slug, '');
        }
      }
    }

    // Find next incomplete visible step
    const nextIndex = visibleAttributes.findIndex(
      (attr, i) => i > index && !ctx.selection.selections[attr.slug]
    );

    if (nextIndex !== -1) {
      expandedStep = nextIndex;
    } else {
      // All attributes selected, open quantity step
      expandedStep = visibleAttributes.length;
    }

    scrollToStep(expandedStep);
  }

  // Handle cell selection in OplageEnLevering table (quantity + delivery + druktype)
  function handleCellSelect(quantity: number, deliveryOption: DeliveryOption, druktype: string) {
    ctx.selection.setQuantityAndDelivery(quantity, deliveryOption, druktype);
  }

  // Handle custom quantity toggle
  function handleToggleCustom() {
    ctx.selection.toggleCustomQuantity();
  }

  // Handle custom quantity change
  function handleCustomQuantityChange(value: number) {
    ctx.selection.customQuantity = value;

    // Check if quantity exceeds the highest tier
    const tiers = ctx.pricing.availableTiers;
    if (tiers.length > 0) {
      const maxTier = Math.max(...tiers.map(t => t.aantal));
      if (value > maxTier) {
        showMaxQuantityModal = true;
        return;
      }
    }

    ctx.selection.applyCustomQuantity(value, ctx.pricing.availableTiers);
    // Reset delivery to normaal so the picker starts clean for the new quantity
    ctx.selection.setDeliveryOption('normaal');
  }

  // Handle delivery option selection from the custom-quantity delivery picker
  function handleSelectDeliveryForCustom(option: DeliveryOption) {
    ctx.selection.setDeliveryOption(option);
  }

  // Handle add to cart
  async function handleAddToCart() {
    await ctx.cart.addToCart();
  }

  // Handle request quote - redirects to quote checkout
  async function handleRequestQuote() {
    await ctx.cart.requestQuote();
  }

  // Get display text for selection
  function getSelectionDisplay(attrName: string): string | undefined {
    const value = ctx.selection.selections[attrName];
    if (!value) return undefined;

    const terms = ctx.productData.getTermsForAttribute(attrName);
    const term = terms.find((t) => t.slug === value);
    return term?.name || value;
  }

  // Get display text for multi-color selection (balloon products)
  function getMultiColorDisplay(): string | undefined {
    const colors = ctx.selection.selectedColors;
    if (colors.length === 0) return undefined;

    const colorAttr = ctx.productData.getAttributesByType("color")[0];
    if (!colorAttr) return sprintf(__('%d kleuren', 'wpc'), colors.length);

    const terms = ctx.productData.getTermsForAttribute(colorAttr.slug);
    const selectedNames = colors.map((slug) => terms.find((t) => t.slug === slug)?.name).filter(Boolean);

    return selectedNames.length <= 2 ? selectedNames.join(", ") : sprintf(__('%d kleuren geselecteerd', 'wpc'), selectedNames.length);
  }

  // Handle continue button click on multi-color selector
  function handleColorContinue(index: number) {
    const nextIndex = visibleAttributes.findIndex(
      (attr, i) => i > index && !ctx.selection.selections[attr.slug]
    );
    expandedStep = nextIndex !== -1 ? nextIndex : visibleAttributes.length;
    scrollToStep(expandedStep);
  }

  // Get display text for quantity
  const quantityDisplay = $derived.by(() => {
    if (ctx.selection.quantity <= 0) return undefined;
    return sprintf(__('%s stuks', 'wpc'), ctx.selection.quantity.toLocaleString("nl-NL"));
  });

  // Build inline style for grid column overrides
  const gridStyle = $derived.by(() => {
    const gc = ctx.productData.configData?.product.grid_columns;
    if (!gc) return '';
    const parts: string[] = [];
    if (gc.mobile) parts.push(`--wpc-grid-cols-mobile: ${gc.mobile}`);
    if (gc.tablet) parts.push(`--wpc-grid-cols-tablet: ${gc.tablet}`);
    if (gc.desktop) parts.push(`--wpc-grid-cols-desktop: ${gc.desktop}`);
    return parts.join('; ');
  });


</script>

<!-- Product Configurator - Modern Accordion UI -->
<div class="wpc-configurator" style={gridStyle}>
  <div class="wpc-container">
    <div class="wpc-layout">
      <!-- Left column: Configurator steps -->
      <div class="wpc-layout__steps">
        {#if ctx.productData.configData}
          <!-- Attribute steps (single-option steps are auto-selected and hidden) -->
          {#each visibleAttributes as attribute, index (attribute.slug)}
            {@const isExpanded = expandedStep === index}
            {@const useMultiColor = ctx.productData.isBalloonProduct && attribute.type === "color"}
            <ConfiguratorStep
              stepNumber={index + 1}
              title={attribute.label}
              state={getStepState(index)}
              selectionDisplay={useMultiColor ? getMultiColorDisplay() : getSelectionDisplay(attribute.slug)}
              expanded={isExpanded}
              onToggle={() => (expandedStep = index)}
            >
              {#if useMultiColor}
                {@const allColorTerms = ctx.productData.getTermsForAttribute(attribute.slug)}
                {@const availableColorSlugs = ctx.selection.getAvailableOptions(attribute.slug)}
                {@const filteredColorTerms = availableColorSlugs.length > 0
                  ? allColorTerms.filter(term => availableColorSlugs.includes(term.slug))
                  : allColorTerms}
                <MultiColorSelector
                  terms={filteredColorTerms}
                  bind:selectedColors={ctx.selection.selectedColors}
                  showContinueButton={true}
                  oncontinue={() => handleColorContinue(index)}
                />
              {:else}
                {@const allTerms = ctx.productData.getTermsForAttribute(attribute.slug)}
                {@const availableSlugs = ctx.selection.getAvailableOptions(attribute.slug)}
                {@const filteredTerms = availableSlugs.length > 0
                  ? allTerms.filter(term => availableSlugs.includes(term.slug))
                  : allTerms}
                <AttributeSelector
                  {attribute}
                  terms={filteredTerms}
                  selectedValue={ctx.selection.selections[attribute.slug]}
                  onSelect={(value) => handleAttributeSelect(index, attribute.slug, value)}
                />
              {/if}
            </ConfiguratorStep>
          {/each}

          <!-- Quantity + Delivery step -->
          {@const isQuantityExpanded = expandedStep === visibleAttributes.length}
          <ConfiguratorStep
            stepNumber={visibleAttributes.length + 1}
            title={__('Kies oplage en productietijd', 'wpc')}
            state={quantityStepState}
            selectionDisplay={quantityDisplay}
            expanded={isQuantityExpanded}
            onToggle={() => (expandedStep = visibleAttributes.length)}
          >
            <OplageEnLevering
              tiers={ctx.pricing.availableTiers}
              spoedleveringOptions={ctx.pricing.spoedleveringOptions}
              levertijdDagen={ctx.productData.configData?.product.levertijd_dagen ?? 5}
              selectedQuantity={ctx.selection.quantity}
              selectedDruktype={ctx.selection.selectedDruktype}
              selectedDeliveryOption={ctx.selection.deliveryOption}
              showInclTax={ctx.pricing.showInclTax}
              taxRate={ctx.pricing.taxRate}
              hasSpoedInVariation={ctx.pricing.hasSpoedInVariation}
              useCustomQuantity={ctx.selection.useCustomQuantity}
              customQuantity={ctx.selection.customQuantity}
              customQuantityError={ctx.selection.customQuantityError}
              disableCustomQuantity={ctx.productData.configData?.product.disable_custom_quantity ?? false}
              onSelectCell={handleCellSelect}
              onToggleCustom={handleToggleCustom}
              onCustomQuantityChange={handleCustomQuantityChange}
              onSelectDeliveryForCustom={handleSelectDeliveryForCustom}
            />
          </ConfiguratorStep>

          <!-- Digitale proef (shown for all print products once quantity is selected) -->
          {#if ctx.selection.quantity > 0 && !ctx.productData.isNonPrintProduct}
            <div class="wpc-mt-4">
              <DeliveryOptions
                selectedOption={ctx.selection.deliveryOption}
                digitaleProef={ctx.selection.digitaleProef}
                onSetDigitaleProef={ctx.selection.setDigitaleProef}
              />
            </div>
          {/if}

          <!-- Pricing summary (above buttons) -->
          <div class="wpc-mt-6">
            <PricingSummary
              pricing={ctx.pricing.pricing}
              quantity={ctx.selection.quantity}
              deliveryDate={ctx.pricing.deliveryDate}
              {completedSteps}
              {totalSteps}
              isComplete={ctx.selection.isComplete}
              showInclTax={ctx.pricing.showInclTax}
              taxRate={ctx.pricing.taxRate}
              totalInclTax={ctx.pricing.totalInclTax}
              subtotalInclTax={ctx.pricing.subtotalInclTax}
              shippingInclTax={ctx.pricing.shippingInclTax}
              unitPriceInclTax={ctx.pricing.unitPriceInclTax}
              onToggleTax={ctx.pricing.toggleTaxDisplay}
            />
          </div>

          <!-- Add to cart actions -->
          <div class="wpc-mt-6">
            <AddToCartActions
              isComplete={ctx.selection.isComplete}
              isAdding={ctx.cart.adding}
              message={ctx.cart.message}
              messageType={ctx.cart.messageType}
              onAddToCart={handleAddToCart}
              onRequestQuote={handleRequestQuote}
            />
          </div>
        {:else}
          <!-- Loading state -->
          <div class="wpc-loading">
            <div class="wpc-loading__skeleton"></div>
            <div class="wpc-loading__skeleton"></div>
            <div class="wpc-loading__skeleton"></div>
          </div>
        {/if}
      </div>

      <!-- Right column: Empty for now, could be used for product info -->
      <div class="wpc-layout__summary"></div>
    </div>
  </div>
</div>

<!-- Debug Widget (Admin Only) -->
{#if showDebug && ctx.productData.configData}
  <DebugWidget {ctx} />
{/if}

<!-- Admin Price Toggle (floating, bottom-left) -->
{#if ctx.pricing.isAdmin && ctx.productData.configData}
  <AdminPriceToggle
    isReseller={ctx.pricing.isReseller}
    onToggle={ctx.pricing.toggleResellerView}
  />
{/if}

<!-- Max quantity exceeded modal -->
<MaxQuantityModal open={showMaxQuantityModal} onClose={() => showMaxQuantityModal = false} />
